import { useMemo, useState } from "react";
import { Search, Swords, Users } from "lucide-react";
import { Input } from "@/OS/components/ui/input";
import type { LobbyServerRecord } from "../net/lobby";
import type { LevelDefinition } from "../levels/types";

interface MultiplayerJoinProps {
  servers: LobbyServerRecord[];
  maps: LevelDefinition[];
  isBusy?: boolean;
  error?: string | null;
  onBack: () => void;
  onJoin: (server: LobbyServerRecord) => void;
}

const filterButtonClass = "font-mono text-[10px] uppercase tracking-[0.28em] transition-colors duration-200 focus-visible:outline-none";

export function MultiplayerJoin({ servers, maps, isBusy, error, onBack, onJoin }: MultiplayerJoinProps) {
  const [query, setQuery] = useState("");
  const [modeFilter, setModeFilter] = useState<"all" | "dm" | "tdm" | "ctf">("all");

  const filteredServers = useMemo(() => {
    return servers.filter((server) => {
      const matchesQuery = [server.name, server.hostName].join(" ").toLowerCase().includes(query.toLowerCase());
      const matchesMode = modeFilter === "all" || server.mode === modeFilter;
      return matchesQuery && matchesMode;
    });
  }, [modeFilter, query, servers]);

  const mapLookup = useMemo(() => new Map(maps.map((map) => [map.id, map])), [maps]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search servers or host callsigns" className="border-white/10 bg-black/20 pl-9 text-white" />
        </div>
        <div className="flex flex-wrap gap-3">
          {(["all", "dm", "tdm", "ctf"] as const).map((filter) => {
            const active = modeFilter === filter;
            return (
              <button key={filter} type="button" className={`${filterButtonClass} ${active ? "text-[#ffb347]" : "text-white/45 hover:text-[#ffb347]"}`} onClick={() => setModeFilter(filter)}>
                {filter}
              </button>
            );
          })}
        </div>
      </div>

      {error ? <div className="border-b border-red-500/30 pb-4 text-sm text-red-200">{error}</div> : null}

      <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
        {filteredServers.map((server) => {
          const map = mapLookup.get(server.mapId);

          return (
            <button key={server.serverId} type="button" className="group flex flex-col gap-4 text-left focus-visible:outline-none" disabled={isBusy} onClick={() => onJoin(server)}>
              {map ? <img src={map.thumbnail} alt={map.name} className="aspect-[16/9] w-full object-cover opacity-90 transition-transform duration-300 group-hover:scale-[1.02]" /> : null}
              <div className="space-y-2 border-b border-white/10 pb-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-mono text-sm uppercase tracking-[0.32em] text-white/88 group-hover:text-[#ffb347]">{server.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/35">{server.mode}</span>
                </div>
                <div className="text-sm text-white/55">Hosted by {server.hostName}</div>
                <div className="space-y-1 pt-1 text-sm text-white/60">
                  <div className="flex items-center gap-2"><Swords className="h-4 w-4 text-[#ffb347]" /> {map?.name ?? server.mapId}</div>
                  <div className="flex items-center gap-2"><Users className="h-4 w-4 text-cyan-200/80" /> {server.currentPlayers}/{server.maxPlayers} pilots</div>
                </div>
                <div className="pt-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[#ffb347]">{isBusy ? "Connecting" : "Join Server"}</div>
              </div>
            </button>
          );
        })}
      </div>

      {!filteredServers.length ? <div className="border-b border-dashed border-white/10 pb-6 text-sm text-white/45">No live Cortex servers matched the current filter.</div> : null}

      <div className="border-t border-white/10 pt-5">
        <button type="button" className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/60 transition-colors duration-200 hover:text-[#ffb347] focus-visible:outline-none focus-visible:text-[#ffb347]" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}
